// service/Reporteria.js
import instance from "../apis/app";
import instanceDownload from "../apis/download";


// ============================================
// Helper interno: construir query string
// ============================================
const buildQuery = (params) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            queryParams.append(key, value);
        }
    });
    const qs = queryParams.toString();
    return qs ? `?${qs}` : '';
};


// Helper interno: descarga de archivo blob desde servidor
const descargarBlob = async (url, filename) => {
    const response = await instanceDownload.get(url, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
};

// Helper: exportar array de objetos a CSV en el cliente
const ExportarReporteACSV = (datos, nombreArchivo) => {
    if (!datos || datos.length === 0) return;
    const headers = Object.keys(datos[0]);
    const escapar = (v) => {
        let s = String(v ?? '');
        // Una celda que parte con = + - @ la ejecuta Excel como fórmula al abrir
        // el CSV. Se antepone una comilla simple para que quede como texto.
        if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [
        headers.map(escapar).join(','),
        ...datos.map(row => headers.map(h => escapar(row[h])).join(','))
    ].join('\r\n');
    // Sin el BOM, Excel abre el CSV en la codificación local y rompe tildes y ñ.
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${nombreArchivo}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};



// ============================================
// 1. REPORTES FINANCIEROS
// ============================================


// 1.1 INGRESOS Y VENTAS
const GetIngresosPorPeriodo = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.fechaInicio) queryParams.append('fechaInicio', params.fechaInicio);
        if (params.fechaFin) queryParams.append('fechaFin', params.fechaFin);
        if (params.agruparPor) queryParams.append('agruparPor', params.agruparPor);

        const queryString = queryParams.toString();
        const url = `/reporteria/ingresos/periodo${queryString ? `?${queryString}` : ''}`;

        const response = await instance.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}


const GetIngresosPorTaller = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.fechaInicio) queryParams.append('fechaInicio', params.fechaInicio);
        if (params.fechaFin) queryParams.append('fechaFin', params.fechaFin);
        if (params.limite) queryParams.append('limite', params.limite);
        if (params.ordenarPor) queryParams.append('ordenarPor', params.ordenarPor);

        const queryString = queryParams.toString();
        const url = `/reporteria/ingresos/talleres${queryString ? `?${queryString}` : ''}`;

        const response = await instance.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}


const GetIngresosPorRecinto = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.fechaInicio) queryParams.append('fechaInicio', params.fechaInicio);
        if (params.fechaFin) queryParams.append('fechaFin', params.fechaFin);

        const queryString = queryParams.toString();
        const url = `/reporteria/ingresos/recintos${queryString ? `?${queryString}` : ''}`;

        const response = await instance.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}


const GetIngresosPorTipoPlan = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.fechaInicio) queryParams.append('fechaInicio', params.fechaInicio);
        if (params.fechaFin) queryParams.append('fechaFin', params.fechaFin);

        const queryString = queryParams.toString();
        const url = `/reporteria/ingresos/tipo-plan${queryString ? `?${queryString}` : ''}`;

        const response = await instance.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}


const GetResumenIngresos = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.fechaInicio) queryParams.append('fechaInicio', params.fechaInicio);
        if (params.fechaFin) queryParams.append('fechaFin', params.fechaFin);

        const queryString = queryParams.toString();
        const url = `/reporteria/ingresos/resumen${queryString ? `?${queryString}` : ''}`;

        const response = await instance.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}


// 1.2 DESCUENTOS Y PRECIOS
const GetAnalisisDescuentos = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.fechaInicio) queryParams.append('fechaInicio', params.fechaInicio);
        if (params.fechaFin) queryParams.append('fechaFin', params.fechaFin);

        const queryString = queryParams.toString();
        const url = `/reporteria/descuentos/analisis${queryString ? `?${queryString}` : ''}`;

        const response = await instance.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}


const GetEvolucionDescuentos = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.fechaInicio) queryParams.append('fechaInicio', params.fechaInicio);
        if (params.fechaFin) queryParams.append('fechaFin', params.fechaFin);
        if (params.agruparPor) queryParams.append('agruparPor', params.agruparPor);

        const queryString = queryParams.toString();
        const url = `/reporteria/descuentos/evolucion${queryString ? `?${queryString}` : ''}`;

        const response = await instance.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}


const GetDistribucionPorTipoPrecio = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.fechaInicio) queryParams.append('fechaInicio', params.fechaInicio);
        if (params.fechaFin) queryParams.append('fechaFin', params.fechaFin);

        const queryString = queryParams.toString();
        const url = `/reporteria/precios/distribucion${queryString ? `?${queryString}` : ''}`;

        const response = await instance.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}


const GetAnalisisMatriculas = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.fechaInicio) queryParams.append('fechaInicio', params.fechaInicio);
        if (params.fechaFin) queryParams.append('fechaFin', params.fechaFin);

        const queryString = queryParams.toString();
        const url = `/reporteria/matriculas/analisis${queryString ? `?${queryString}` : ''}`;

        const response = await instance.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}


// ============================================
// 2. REPORTES DEMOGRÁFICOS
// ============================================


/**
 * Obtener distribución demográfica completa de usuarios
 * @param {Object} params - { incluirSoloInscritos, fechaInicio, fechaFin }
 */
const GetDistribucionDemografica = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.incluirSoloInscritos !== undefined) {
            queryParams.append('incluirSoloInscritos', params.incluirSoloInscritos);
        }
        if (params.fechaInicio) queryParams.append('fechaInicio', params.fechaInicio);
        if (params.fechaFin) queryParams.append('fechaFin', params.fechaFin);

        const queryString = queryParams.toString();
        const url = `/reporteria/demograficos/distribucion${queryString ? `?${queryString}` : ''}`;

        const response = await instance.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}


/**
 * Obtener top comunas con más usuarios
 * @param {Object} params - { limite, incluirSoloInscritos }
 */
const GetTopComunas = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.limite) queryParams.append('limite', params.limite);
        if (params.incluirSoloInscritos !== undefined) {
            queryParams.append('incluirSoloInscritos', params.incluirSoloInscritos);
        }

        const queryString = queryParams.toString();
        const url = `/reporteria/demograficos/top-comunas${queryString ? `?${queryString}` : ''}`;

        const response = await instance.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}


/**
 * Análisis de usuarios vecinos vs no vecinos
 * @param {Object} params - { fechaInicio, fechaFin, comunaReferencia }
 */
const GetAnalisisVecinos = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.fechaInicio) queryParams.append('fechaInicio', params.fechaInicio);
        if (params.fechaFin) queryParams.append('fechaFin', params.fechaFin);
        if (params.comunaReferencia) queryParams.append('comunaReferencia', params.comunaReferencia);

        const queryString = queryParams.toString();
        const url = `/reporteria/demograficos/analisis-vecinos${queryString ? `?${queryString}` : ''}`;

        const response = await instance.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}


/**
 * Obtener estadísticas detalladas de edad
 * @param {Object} params - { incluirSoloInscritos }
 */
const GetEstadisticasEdad = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.incluirSoloInscritos !== undefined) {
            queryParams.append('incluirSoloInscritos', params.incluirSoloInscritos);
        }

        const queryString = queryParams.toString();
        const url = `/reporteria/demograficos/estadisticas-edad${queryString ? `?${queryString}` : ''}`;

        const response = await instance.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}




// 2.1 DESCARGAS CSV DEMOGRÁFICOS

/**
 * Descargar distribución por edad en CSV
 * @param {Object} params - { incluirSoloInscritos, fechaInicio, fechaFin }
 * @param {String} filename - nombre del archivo (opcional)
 */
const DescargarCSVEdad = async (params = {}, filename = null) => {
    try {
        const url = `/reporteria/demograficos/descargar/edad${buildQuery({
            ...(params.incluirSoloInscritos !== undefined && { incluirSoloInscritos: params.incluirSoloInscritos }),
            ...(params.fechaInicio && { fechaInicio: params.fechaInicio }),
            ...(params.fechaFin && { fechaFin: params.fechaFin })
        })}`;

        const today = new Date().toISOString().split('T')[0];
        await descargarBlob(url, filename || `distribucion_edad_${today}.csv`);
    } catch (error) {
        throw error.response?.data || error;
    }
}


/**
 * Descargar distribución por género en CSV
 * @param {Object} params - { incluirSoloInscritos, fechaInicio, fechaFin }
 * @param {String} filename - nombre del archivo (opcional)
 */
const DescargarCSVGenero = async (params = {}, filename = null) => {
    try {
        const url = `/reporteria/demograficos/descargar/genero${buildQuery({
            ...(params.incluirSoloInscritos !== undefined && { incluirSoloInscritos: params.incluirSoloInscritos }),
            ...(params.fechaInicio && { fechaInicio: params.fechaInicio }),
            ...(params.fechaFin && { fechaFin: params.fechaFin })
        })}`;

        const today = new Date().toISOString().split('T')[0];
        await descargarBlob(url, filename || `distribucion_genero_${today}.csv`);
    } catch (error) {
        throw error.response?.data || error;
    }
}


/**
 * Descargar distribución por comunas en CSV
 * @param {Object} params - { incluirSoloInscritos, fechaInicio, fechaFin, limite }
 * @param {String} filename - nombre del archivo (opcional)
 */
const DescargarCSVComunas = async (params = {}, filename = null) => {
    try {
        const url = `/reporteria/demograficos/descargar/comunas${buildQuery({
            ...(params.incluirSoloInscritos !== undefined && { incluirSoloInscritos: params.incluirSoloInscritos }),
            ...(params.fechaInicio && { fechaInicio: params.fechaInicio }),
            ...(params.fechaFin && { fechaFin: params.fechaFin }),
            ...(params.limite && { limite: params.limite })
        })}`;

        const today = new Date().toISOString().split('T')[0];
        await descargarBlob(url, filename || `distribucion_comunas_${today}.csv`);
    } catch (error) {
        throw error.response?.data || error;
    }
}


// ============================================
// 3. REPORTES DE COMPORTAMIENTO
// ============================================


const GetAnalisisRetencion = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.fechaInicio) queryParams.append('fechaInicio', params.fechaInicio);
        if (params.fechaFin) queryParams.append('fechaFin', params.fechaFin);
        if (params.periodoAnalisis) queryParams.append('periodoAnalisis', params.periodoAnalisis);

        const queryString = queryParams.toString();
        const url = `/reporteria/comportamiento/retencion${queryString ? `?${queryString}` : ''}`;

        const response = await instance.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}


const GetAnalisisRecurrencia = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.fechaInicio) queryParams.append('fechaInicio', params.fechaInicio);
        if (params.fechaFin) queryParams.append('fechaFin', params.fechaFin);

        const queryString = queryParams.toString();
        const url = `/reporteria/comportamiento/recurrencia${queryString ? `?${queryString}` : ''}`;

        const response = await instance.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}


const GetAnalisisChurn = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.mesesInactividad) queryParams.append('mesesInactividad', params.mesesInactividad);

        const queryString = queryParams.toString();
        const url = `/reporteria/comportamiento/churn${queryString ? `?${queryString}` : ''}`;

        const response = await instance.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}


const GetAnalisisLTV = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.segmentar !== undefined) queryParams.append('segmentar', params.segmentar);

        const queryString = queryParams.toString();
        const url = `/reporteria/comportamiento/ltv${queryString ? `?${queryString}` : ''}`;

        const response = await instance.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}


// ============================================
// 4. VENTAS UNIFICADAS (presencial + online)
// ============================================


const GetVentasUnificadas = async (params = {}) => {
    try {
        const url = `/reporteria/ventas-unificadas${buildQuery(params)}`;
        const response = await instance.get(url);
        console.log('Respuesta de ventas unificadas:', response);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};


// El Excel se genera en segundo plano: la API encola el trabajo, una Lambda arma
// el archivo y lo deja en S3, y acá se consulta el estado hasta que llega la URL
// de descarga. Bajarlo en la misma request no daba para rangos largos (1-ene a
// hoy, todos los recintos = ~76.000 filas): tardaba más que los 29 s de API
// Gateway y el archivo pasaba el límite de 6 MB de respuesta de Lambda.
const ESPERA_ENTRE_CONSULTAS_MS = 3000;
const ESPERA_MAXIMA_MS = 17 * 60 * 1000; // la Lambda de exportación corta a los 15 min

const esperar = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * @param {Object} params filtros del listado (sin page/limit)
 * @param {Object} [opciones]
 * @param {Function} [opciones.onEstado] recibe el estado de cada consulta
 *   ('pendiente' | 'procesando' | 'lista'), para mostrar el avance.
 */
const ExportarVentasUnificadasExcel = async (params = {}, { onEstado } = {}) => {
    try {
        // La instancia ya devuelve el cuerpo (interceptor res => res.data).
        const inicio = await instance.post('/reporteria/ventas-unificadas/exportaciones', params);
        const id = inicio.data.id;

        const limite = Date.now() + ESPERA_MAXIMA_MS;
        while (Date.now() < limite) {
            await esperar(ESPERA_ENTRE_CONSULTAS_MS);

            let estado;
            try {
                ({ data: estado } = await instance.get(`/reporteria/ventas-unificadas/exportaciones/${id}`));
            } catch (error) {
                // Un tropiezo de red en una consulta no es motivo para abandonar un
                // archivo que se sigue generando; los 4XX sí son definitivos.
                const status = error.response?.status;
                if (status && status >= 400 && status < 500) throw error;
                continue;
            }

            onEstado?.(estado.estado);
            if (estado.estado === 'error') {
                throw new Error(estado.error || 'No se pudo generar el archivo');
            }
            if (estado.estado === 'lista') {
                // URL prefirmada de S3 que ya trae Content-Disposition: attachment.
                // Se navega directo, sin pasar el archivo por memoria.
                const link = document.createElement('a');
                link.href = estado.url;
                link.setAttribute('download', estado.nombreArchivo || 'ventas-unificadas.xlsx');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                return estado;
            }
        }
        throw new Error('La exportación está tardando demasiado. Intenta con un rango más corto.');
    } catch (error) {
        throw error.response?.data || error;
    }
};


// ============================================
// 5. VENTAS POR TALLER
// ============================================


const GetEstadisticasVentas = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.fechaInicio) queryParams.append('fechaInicio', params.fechaInicio);
        if (params.fechaFin) queryParams.append('fechaFin', params.fechaFin);

        const queryString = queryParams.toString();
        const url = `/reporteria/ventas/estadisticas-generales${queryString ? `?${queryString}` : ''}`;

        const response = await instance.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}


const GetTopTalleres = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.limite) queryParams.append('limite', params.limite);
        if (params.ordenarPor) queryParams.append('ordenarPor', params.ordenarPor);
        if (params.fechaInicio) queryParams.append('fechaInicio', params.fechaInicio);
        if (params.fechaFin) queryParams.append('fechaFin', params.fechaFin);

        const queryString = queryParams.toString();
        const url = `/reporteria/ventas/top-talleres${queryString ? `?${queryString}` : ''}`;

        const response = await instance.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}


const CompararTalleres = async (tallerIds, params = {}) => {
    try {
        const response = await instance.post('/reporteria/ventas/comparar', {
            tallerIds,
            fechaInicio: params.fechaInicio,
            fechaFin: params.fechaFin
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}


const GetMatrizRentabilidad = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.fechaInicio) queryParams.append('fechaInicio', params.fechaInicio);
        if (params.fechaFin) queryParams.append('fechaFin', params.fechaFin);

        const queryString = queryParams.toString();
        const url = `/reporteria/ventas/matriz-rentabilidad${queryString ? `?${queryString}` : ''}`;

        const response = await instance.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}


const GetEvolucionVentas = async (params = {}) => {
    try {
        const queryParams = new URLSearchParams();
        if (params.meses) queryParams.append('meses', params.meses);
        if (params.agrupacion) queryParams.append('agrupacion', params.agrupacion);

        const queryString = queryParams.toString();
        const url = `/reporteria/ventas/evolucion${queryString ? `?${queryString}` : ''}`;

        const response = await instance.get(url);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
}


export default {
    // 1. REPORTES FINANCIEROS
    GetIngresosPorPeriodo,
    GetIngresosPorTaller,
    GetIngresosPorRecinto,
    GetIngresosPorTipoPlan,
    GetResumenIngresos,
    GetAnalisisDescuentos,
    GetEvolucionDescuentos,
    GetDistribucionPorTipoPrecio,
    GetAnalisisMatriculas,

    // 2. REPORTES DEMOGRÁFICOS
    GetDistribucionDemografica,
    GetTopComunas,
    GetAnalisisVecinos,
    GetEstadisticasEdad,
    DescargarCSVEdad,
    DescargarCSVGenero,
    DescargarCSVComunas,

    // 3. REPORTES DE COMPORTAMIENTO
    GetAnalisisRetencion,
    GetAnalisisRecurrencia,
    GetAnalisisChurn,
    GetAnalisisLTV,

    // 4. VENTAS UNIFICADAS
    GetVentasUnificadas,
    ExportarVentasUnificadasExcel,

    // 5. VENTAS POR TALLER
    GetEstadisticasVentas,
    GetTopTalleres,
    CompararTalleres,
    GetMatrizRentabilidad,
    GetEvolucionVentas,

    // UTILIDADES
    ExportarReporteACSV,
};
